import { useEffect, useState } from "react";
import { api, fmtMoney, fmtDate } from "../lib/api";
import { Drawer, Field } from "../components/UI";
import { Page } from "../components/AppShell";
import { Plus, Trash, DownloadSimple } from "@phosphor-icons/react";

const downloadPdf = async (path, filename) => {
  const r = await api.get(path, { responseType: "blob" });
  const url = URL.createObjectURL(r.data);
  const a = document.createElement("a"); a.href = url; a.download = filename;
  document.body.appendChild(a); a.click(); a.remove();
  URL.revokeObjectURL(url);
};

const PdfBtn = ({ path, filename, testid }) => (
  <button className="btn btn-secondary btn-sm" onClick={() => downloadPdf(path, filename)} data-testid={testid}>
    <DownloadSimple size={12} /> DOWNLOAD PDF
  </button>
);

export default function Accounting() {
  const [tab, setTab] = useState("journal");
  return (
    <Page
      title="Accounting"
      subtitle="FINANCE / GENERAL LEDGER"
      breadcrumbs={[{ label: "Dashboard", to: "/" }, "Accounting"]}
      testid="accounting-page"
    >
      <div className="flex gap-1 border-b border-border mb-6">
        {[["journal","Journal Entries"],["accounts","Chart of Accounts"],["pnl","P&L"],["bs","Balance Sheet"],["ar","AR Aging"]].map(([k,l]) => (
          <button key={k} className={`px-4 py-2 text-sm border-b-2 -mb-px ${tab===k?"border-primary text-primary font-semibold":"border-transparent text-muted-foreground hover:text-foreground"}`} onClick={() => setTab(k)} data-testid={`acct-tab-${k}`}>{l}</button>
        ))}
      </div>
      {tab === "journal" && <Journal />}
      {tab === "accounts" && <ChartOfAccounts />}
      {tab === "pnl" && <PnL />}
      {tab === "bs" && <BalanceSheet />}
      {tab === "ar" && <ARAging />}
    </Page>
  );
}

function Journal() {
  const [items, setItems] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ memo: "", lines: [{account_id:"",debit:"0",credit:"0"},{account_id:"",debit:"0",credit:"0"}] });
  const load = async () => {
    const [j, a] = await Promise.all([api.get("/accounting/journal"), api.get("/accounting/accounts")]);
    setItems(j.data); setAccounts(a.data);
  };
  useEffect(() => { load(); }, []);
  const aName = (id) => { const a = accounts.find(x=>x.id===id); return a?`${a.code} ${a.name}`:"—"; };
  const submit = async () => {
    try {
      await api.post("/accounting/journal", { memo: form.memo, lines: form.lines.map(l => ({ account_id: l.account_id, debit: parseFloat(l.debit||"0"), credit: parseFloat(l.credit||"0") })) });
      setOpen(false); setForm({ memo: "", lines: [{account_id:"",debit:"0",credit:"0"},{account_id:"",debit:"0",credit:"0"}] }); load();
    } catch (e) { alert(e?.response?.data?.detail || "Error"); }
  };
  return (
    <>
      <div className="flex justify-end mb-3"><button className="btn btn-primary" onClick={() => setOpen(true)} data-testid="new-journal-btn"><Plus size={14}/> NEW ENTRY</button></div>
      <div className="surface"><table className="dense">
        <thead><tr><th>ENTRY</th><th>DATE</th><th>MEMO</th><th>SOURCE</th><th>LINES</th><th className="num">DEBIT</th><th className="num">CREDIT</th></tr></thead>
        <tbody>
          {items.length === 0 && <tr><td colSpan="7" className="text-center text-muted-foreground py-12">No journal entries</td></tr>}
          {items.map(j => {
            const debit = j.lines.reduce((s,l)=>s+Number(l.debit),0);
            const credit = j.lines.reduce((s,l)=>s+Number(l.credit),0);
            return (
              <tr key={j.id}>
                <td className="mono">{j.entry_number}</td>
                <td className="text-xs">{fmtDate(j.date)}</td>
                <td>{j.memo}</td>
                <td><span className="badge badge-gray">{j.source_type || "manual"}</span></td>
                <td className="text-xs">{j.lines.map(l=><div key={l.id}>{aName(l.account_id)}</div>)}</td>
                <td className="num">{fmtMoney(debit)}</td>
                <td className="num">{fmtMoney(credit)}</td>
              </tr>
            );
          })}
        </tbody>
      </table></div>
      <Drawer open={open} onClose={()=>setOpen(false)} title="New journal entry" size="wide" testid="new-journal-drawer"
        footer={<><button className="btn btn-secondary" onClick={()=>setOpen(false)}>CANCEL</button><button className="btn btn-primary" onClick={submit} data-testid="save-journal-btn">POST</button></>}>
        <Field label="MEMO"><input className="input" value={form.memo} onChange={e => setForm({...form, memo: e.target.value})} /></Field>
        <div className="label-xs mt-3 mb-2">LINES</div>
        {form.lines.map((l,i) => (
          <div key={i} className="grid grid-cols-12 gap-2 mb-2">
            <select className="input col-span-6" value={l.account_id} onChange={e => { const ls=[...form.lines]; ls[i].account_id=e.target.value; setForm({...form, lines:ls}); }}>
              <option value="">Account...</option>
              {accounts.map(a => <option key={a.id} value={a.id}>{a.code} {a.name}</option>)}
            </select>
            <input className="input col-span-3 mono" placeholder="Debit" value={l.debit} onChange={e => { const ls=[...form.lines]; ls[i].debit=e.target.value; setForm({...form, lines:ls}); }} />
            <input className="input col-span-3 mono" placeholder="Credit" value={l.credit} onChange={e => { const ls=[...form.lines]; ls[i].credit=e.target.value; setForm({...form, lines:ls}); }} />
          </div>
        ))}
        <button className="btn btn-secondary btn-sm" onClick={() => setForm({...form, lines:[...form.lines,{account_id:"",debit:"0",credit:"0"}]})}>+ ADD LINE</button>
      </Drawer>
    </>
  );
}

function ChartOfAccounts() {
  const [items, setItems] = useState([]);
  useEffect(() => { api.get("/accounting/accounts").then(r => setItems(r.data)); }, []);
  return (
    <div className="surface"><table className="dense">
      <thead><tr><th>CODE</th><th>NAME</th><th>TYPE</th><th>SYSTEM</th></tr></thead>
      <tbody>{items.map(a => (
        <tr key={a.id}><td className="mono">{a.code}</td><td>{a.name}</td><td><span className="badge badge-default">{a.type}</span></td><td className="text-xs">{a.is_system?"YES":"—"}</td></tr>
      ))}</tbody>
    </table></div>
  );
}

function PnL() {
  const [d, setD] = useState(null);
  useEffect(() => { api.get("/accounting/reports/pnl").then(r => setD(r.data)); }, []);
  if (!d) return <div className="text-muted-foreground">Loading...</div>;
  return (
    <div>
      <div className="flex justify-end mb-3"><PdfBtn path="/accounting/reports/pnl/pdf" filename="profit_and_loss.pdf" testid="pnl-pdf-btn" /></div>
      <div className="surface p-6 max-w-2xl">
        <div className="label-xs mb-3">PROFIT & LOSS</div>
        <div className="space-y-1 mb-4">
          <div className="font-bold mb-1">Revenue</div>
          {d.rows.filter(r=>r.type==="revenue").map(r => (<div key={r.code} className="flex justify-between text-sm pl-4"><span className="mono">{r.code} {r.name}</span><span className="mono">{fmtMoney(r.balance)}</span></div>))}
          <div className="flex justify-between font-semibold border-t pt-1"><span>Total Revenue</span><span className="mono">{fmtMoney(d.total_revenue)}</span></div>
        </div>
        <div className="space-y-1 mb-4">
          <div className="font-bold mb-1">Expenses</div>
          {d.rows.filter(r=>r.type==="expense").map(r => (<div key={r.code} className="flex justify-between text-sm pl-4"><span className="mono">{r.code} {r.name}</span><span className="mono">{fmtMoney(r.balance)}</span></div>))}
          <div className="flex justify-between font-semibold border-t pt-1"><span>Total Expenses</span><span className="mono">{fmtMoney(d.total_expense)}</span></div>
        </div>
        <div className="flex justify-between heading text-xl border-t-2 border-foreground pt-2"><span>Net Income</span><span className="mono">{fmtMoney(d.net_income)}</span></div>
      </div>
    </div>
  );
}

function BalanceSheet() {
  const [d, setD] = useState(null);
  useEffect(() => { api.get("/accounting/reports/balance-sheet").then(r => setD(r.data)); }, []);
  if (!d) return null;
  const Group = ({ type, label }) => (
    <div className="space-y-1 mb-4">
      <div className="font-bold mb-1">{label}</div>
      {d.rows.filter(r=>r.type===type).map(r => (<div key={r.code} className="flex justify-between text-sm pl-4"><span className="mono">{r.code} {r.name}</span><span className="mono">{fmtMoney(r.balance)}</span></div>))}
      <div className="flex justify-between font-semibold border-t pt-1"><span>Total {label}</span><span className="mono">{fmtMoney(d.totals[type])}</span></div>
    </div>
  );
  return (
    <div>
      <div className="flex justify-end mb-3"><PdfBtn path="/accounting/reports/balance-sheet/pdf" filename="balance_sheet.pdf" testid="bs-pdf-btn" /></div>
      <div className="surface p-6 max-w-2xl">
        <div className="label-xs mb-3">BALANCE SHEET</div>
        <Group type="asset" label="Assets" />
        <Group type="liability" label="Liabilities" />
        <Group type="equity" label="Equity" />
      </div>
    </div>
  );
}

function ARAging() {
  const [d, setD] = useState(null);
  useEffect(() => { api.get("/accounting/reports/ar-aging").then(r => setD(r.data)); }, []);
  if (!d) return null;
  return (
    <div>
      <div className="flex justify-end mb-3"><PdfBtn path="/accounting/reports/ar-aging/pdf" filename="ar_aging.pdf" testid="ar-pdf-btn" /></div>
      <div className="grid grid-cols-5 gap-3 mb-6">
        {[["current","CURRENT"],["1_30","1-30"],["31_60","31-60"],["61_90","61-90"],["over_90","90+"]].map(([k,l]) => (
          <div key={k} className="surface p-4"><div className="label-xs">{l}</div><div className="heading text-2xl mono">{fmtMoney(d.buckets[k])}</div></div>
        ))}
      </div>
      <div className="surface"><table className="dense">
        <thead><tr><th>INVOICE</th><th>CUSTOMER</th><th>DUE</th><th>DAYS OVERDUE</th><th>BUCKET</th><th className="num">OUTSTANDING</th></tr></thead>
        <tbody>
          {d.rows.length === 0 && <tr><td colSpan="6" className="text-center text-muted-foreground py-12">No outstanding invoices</td></tr>}
          {d.rows.map((r,i) => (<tr key={i}><td className="mono">{r.invoice_number}</td><td>{r.customer}</td><td className="text-xs">{fmtDate(r.due_date)}</td><td className="mono">{r.days_overdue}</td><td><span className="badge badge-default">{r.bucket}</span></td><td className="num">{fmtMoney(r.outstanding)}</td></tr>))}
        </tbody>
      </table></div>
    </div>
  );
}
